import type { ReactNode } from 'react';
import { useMemo, useState, useContext, useCallback, createContext } from 'react';

const STORAGE_KEY = 'somnus-consent';
const CONSENT_VERSION = 1;

export type ConsentCategories = {
  analytics: boolean;
};

type ConsentContextValue = {
  consent: ConsentCategories | null;
  settingsOpen: boolean;
  openSettings: () => void;
  closeSettings: () => void;
  saveConsent: (categories: ConsentCategories) => void;
};

const ConsentContext = createContext<ConsentContextValue | null>(null);

function readStoredConsent(): ConsentCategories | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);

    if (!raw) {
      return null;
    }

    const parsed: unknown = JSON.parse(raw);

    if (typeof parsed !== 'object' || parsed === null) {
      return null;
    }

    if (!('version' in parsed) || parsed.version !== CONSENT_VERSION) {
      return null;
    }

    if (!('categories' in parsed)) {
      return null;
    }

    const { categories } = parsed;

    if (typeof categories !== 'object' || categories === null) {
      return null;
    }

    if (!('analytics' in categories) || typeof categories.analytics !== 'boolean') {
      return null;
    }

    return { analytics: categories.analytics };
  } catch {
    return null;
  }
}

function writeStoredConsent(categories: ConsentCategories) {
  const payload = {
    categories,
    version: CONSENT_VERSION,
    savedAt: new Date().toISOString(),
  };

  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    return;
  }
}

export function ConsentProvider({ children }: { children: ReactNode }) {
  const [consent, setConsent] = useState<ConsentCategories | null>(readStoredConsent);
  const [settingsOpen, setSettingsOpen] = useState(false);

  const openSettings = useCallback(() => {
    setSettingsOpen(true);
  }, []);

  const closeSettings = useCallback(() => {
    setSettingsOpen(false);
  }, []);

  const saveConsent = useCallback((categories: ConsentCategories) => {
    writeStoredConsent(categories);
    setConsent(categories);
    setSettingsOpen(false);
  }, []);

  const value = useMemo(
    () => ({ consent, saveConsent, settingsOpen, openSettings, closeSettings }),
    [consent, settingsOpen, openSettings, closeSettings, saveConsent]
  );

  return <ConsentContext.Provider value={value}>{children}</ConsentContext.Provider>;
}

export function useConsent() {
  const context = useContext(ConsentContext);

  if (!context) {
    throw new Error('useConsent must be used within a ConsentProvider');
  }

  return context;
}
